import { useState, useEffect, useRef } from "react"
import SourceEditor from "aldoo-ra/SourceEditor"
import "./style.css"
import "./plugins/cursor-vis/style.css"
import Toolbar from "./toolbar"
import Typography from "aldoo-ra/Typography"
import PubSub from "pubsub-js"
import { useStyleGeneration } from "./StyleClassEditor/hooks/useStyleGeneration"
import { HtmlEditorCommander } from "aldoo-ra/Commander"

// Import existing plugins
import Align, {
  setCurrentSelectedImage,
  clearCurrentSelectedImage,
} from "./plugins/align"
import TypographyPlugin from "./plugins/typography"
import Color from "./plugins/color"
import FontSize from "./plugins/font-size"
import History from "./plugins/history"
import Image from "./plugins/image"
import Button from "./plugins/button"
import Link from "./plugins/link"
import PagePreview from "./plugins/page-preview"
import CursorVis from "./plugins/cursor-vis"

// Import new management plugins
import ContentManager from "./plugins/content-manager"
import DOMManager from "./plugins/dom-manager"
import EventManager from "./plugins/event-manager"
import SelectionManager from "./plugins/selection-manager"
import StyleManager from "./plugins/style-manager"

// Import utility functions from Image plugin
const { url, processCDNAttributes } = Image.utils

// Simple CDN URL processor for external use (e.g., blog posts)
export const updateCDNUrls = (htmlString) => {
  if (!htmlString || typeof htmlString !== "string") return ""

  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlString, "text/html")
  const elements = doc.querySelectorAll("[cdn]")

  elements.forEach((element) => {
    const cdnValue = element.getAttribute("cdn")
    if (cdnValue) {
      const fullUrl = url(cdnValue)
      if (element.tagName.toLowerCase() === "img") {
        element.setAttribute("src", fullUrl)
      } else if (element.tagName.toLowerCase() === "a") {
        element.setAttribute("href", fullUrl)
      } else if (
        element.tagName.toLowerCase() === "video" ||
        element.tagName.toLowerCase() === "audio"
      ) {
        element.setAttribute("src", fullUrl)
      } else {
        element.setAttribute("src", fullUrl)
      }
    }
  })

  return doc.body.innerHTML
}

// Function to prepare content for saving - removes expanded URLs
const prepareContentForSave = (html) => {
  if (!html) return ""

  // Process CDN attributes using string replacement to preserve HTML formatting
  // Remove src/href attributes from elements that have cdn attributes
  let processedHtml = html

  // Pattern to match elements with cdn attribute and remove their src/href
  // This preserves the original HTML structure while removing expanded URLs
  processedHtml = processedHtml.replace(
    /<(img|video|audio)\s+([^>]*?)cdn\s*=\s*"[^"]*"([^>]*?)src\s*=\s*"[^"]*"([^>]*?)>/gi,
    '<$1 $2cdn="$3"$4>'
  )

  processedHtml = processedHtml.replace(
    /<(img|video|audio)\s+([^>]*?)src\s*=\s*"[^"]*"([^>]*?)cdn\s*=\s*"[^"]*"([^>]*?)>/gi,
    '<$1 $2$3cdn="$4">'
  )

  // Handle anchor tags with cdn and href
  processedHtml = processedHtml.replace(
    /<a\s+([^>]*?)cdn\s*=\s*"[^"]*"([^>]*?)href\s*=\s*"[^"]*"([^>]*?)>/gi,
    '<a $1cdn="$2"$3>'
  )

  processedHtml = processedHtml.replace(
    /<a\s+([^>]*?)href\s*=\s*"[^"]*"([^>]*?)cdn\s*=\s*"[^"]*"([^>]*?)>/gi,
    '<a $1$2cdn="$3">'
  )

  return processedHtml
}

const HtmlEditor = ({
  content = "",
  onUpdate,
  title = "HTML Editor",
  readOnly = false,
  save,
  variables = {}, // Object with available variables for insertion
}) => {
  // Use shared HtmlEditor Commander instance
  const { runCommand, plugins, setStateResolver } = HtmlEditorCommander

  const editorRef = useRef(null)
  const [isSourceEditorOpen, setIsSourceEditorOpen] = useState(false)
  const [sourceEditorContent, setSourceEditorContent] = useState("")
  const [currentContent, setCurrentContent] = useState("")
  const [isPropertyEditorActive, setIsPropertyEditorActive] = useState(false)
  const [selectionRange, setSelectionRange] = useState(null)
  const isProcessingContentUpdateRef = useRef(false)

  // Plugin instances
  const contentManagerRef = useRef(null)
  const domManagerRef = useRef(null)
  const eventManagerRef = useRef(null)
  const selectionManagerRef = useRef(null)
  const styleManagerRef = useRef(null)

  // Register plugins with this editor's Commander instance
  useEffect(() => {
    plugins({
      align: Align,
      typography: TypographyPlugin,
      color: Color,
      fontSize: FontSize,
      history: History,
      button: [Button, { useHistory: ["add", "style", "align"] }],
      image: [Image, { useHistory: ["add"] }],
      link: [Link, { useHistory: ["add"] }],
      pagePreview: PagePreview,
      cursorVis: CursorVis,
    })
  }, [])

  // Use StyleClassEditor hooks for proper HTML/CSS generation
  const { generateUpdatedHTML, extractStyleContent } = useStyleGeneration()

  // Initialize plugins
  useEffect(() => {
    // Create plugin instances
    contentManagerRef.current = ContentManager.create()
    domManagerRef.current = DOMManager.create()
    eventManagerRef.current = EventManager.create()
    selectionManagerRef.current = SelectionManager.create()
    styleManagerRef.current = StyleManager.create()

    // Initialize content manager
    contentManagerRef.current.initialize({
      editorRef,
      content,
      onUpdate: (newContent) => {
        setCurrentContent(newContent)
        if (onUpdate) {
          onUpdate(newContent)
        }
      },
      generateUpdatedHTML,
      extractStyleContent,
      url,
      processCDNAttributes,
      styleManager: styleManagerRef.current,
    })

    // Initialize DOM manager
    domManagerRef.current.initialize({ editorRef })

    // Initialize selection manager
    selectionManagerRef.current.initialize({ editorRef })

    // Initialize event manager
    eventManagerRef.current.initialize({
      editorRef,
      readOnly,
      isProcessingContentUpdateRef,
      isPropertyEditorActive,
      variables,
      onSelectionChange: () => {
        const range = selectionManagerRef.current.updateSelection()
        setSelectionRange(range)
      },
      onInput: () => {
        // Handle content updates
        const completeDocument = contentManagerRef.current.getCompleteDocument()
        setCurrentContent(completeDocument)
        if (onUpdate) {
          onUpdate(completeDocument)
        }
      },
      domManager: domManagerRef.current,
    })

    // Initialize style manager
    styleManagerRef.current.initialize()

    // Initialize cursor-vis plugin
    runCommand("cursorVis.init", {
      container: editorRef.current,
      onImageMoved: (imageElement) => {
        // Handle image moved callback if needed
        const completeDocument = contentManagerRef.current.getCompleteDocument()
        setCurrentContent(completeDocument)
        if (onUpdate) {
          onUpdate(completeDocument)
        }
      }
    })

    return () => {
      // Cleanup plugins
      contentManagerRef.current?.destroy()
      domManagerRef.current?.destroy()
      eventManagerRef.current?.destroy()
      styleManagerRef.current?.destroy()
      runCommand("cursorVis.destroy")
    }
  }, [])

  // Update content when prop changes
  useEffect(() => {
    if (contentManagerRef.current && content !== currentContent) {
      contentManagerRef.current.updateContent(content)
      setCurrentContent(content)
    }
  }, [content])

  // Set up state resolver for history
  useEffect(() => {
    setStateResolver(() => {
      return contentManagerRef.current?.getCompleteDocument() || currentContent
    })

    // Clear history on mount
    runCommand("history.clear")
  }, [])

  // Subscribe to property editor state changes
  useEffect(() => {
    const editorStateToken = PubSub.subscribe(
      "property-editor:state",
      (msg, data) => {
        if (data.state === "opened") {
          setIsPropertyEditorActive(true)
        } else if (data.state === "closed") {
          setIsPropertyEditorActive(false)
        }
      }
    )

    return () => {
      PubSub.unsubscribe(editorStateToken)
    }
  }, [])

  // Update event manager settings when props change
  useEffect(() => {
    if (eventManagerRef.current) {
      eventManagerRef.current.updateSettings({
        readOnly,
        isPropertyEditorActive,
        variables,
      })
    }
  }, [readOnly, isPropertyEditorActive, variables])

  const insertImage = async () => {
    const result = await runCommand("image.add", {
      selectionRange,
      setSelectionRange,
    })
    return result
  }

  const insertButton = () => {
    return runCommand("button.add", {
      selectionRange,
      setSelectionRange,
    })
  }

  const insertLink = async () => {
    const result = await runCommand("link.add", {
      selectionRange,
      setSelectionRange,
      variables,
    })

    if (result && result.hasAttribute && result.hasAttribute("cdn")) {
      processCDNAttributes(result)
    }

    return result
  }

  const editHtmlContent = () => {
    const contentForSource = contentManagerRef.current?.getCompleteDocument() || currentContent
    setSourceEditorContent(contentForSource)
    setIsSourceEditorOpen(true)
  }

  const handleHtmlUpdate = (newContent, options = {}) => {
    const { fromSourceEditor = false } = options

    if (contentManagerRef.current) {
      contentManagerRef.current.updateContent(newContent)
    }

    setCurrentContent(newContent)

    if (fromSourceEditor) {
      setIsSourceEditorOpen(false)
    }

    if (onUpdate && newContent !== currentContent) {
      const preparedContent = prepareContentForSave(newContent)
      onUpdate(preparedContent)
    }

    return newContent
  }

  const undo = () => {
    const restore = runCommand("history.undo")
    if (restore !== undefined) {
      handleHtmlUpdate(restore)
    }
  }

  const redo = () => {
    const restore = runCommand("history.redo")
    if (restore) {
      handleHtmlUpdate(restore)
    }
  }

  const togglePreview = () => {
    runCommand("pagePreview.toggle", {
      getContent: editor.getContent,
      openHtmlEditor: editHtmlContent,
    })
  }

  const handleSave = () => {
    if (readOnly || !save) return

    const contentFromManager = contentManagerRef.current?.getCompleteDocument()
    const contentToSave = contentFromManager || currentContent

    setCurrentContent(contentToSave)
    save(contentToSave)
  }

  // Prevent scroll propagation when editor is at boundaries
  const handleEditorScroll = (e) => {
    const element = e.currentTarget
    const isAtTop = element.scrollTop === 0
    const isAtBottom = element.scrollHeight - element.scrollTop === element.clientHeight

    // Prevent parent scroll when at boundaries
    if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  // Editor API object
  const editor = {
    runCommand,
    insertImage,
    insertLink,
    insertButton,
    editHtmlContent,
    undo,
    redo,
    save: handleSave,
    readOnly,
    togglePreview,
    processCDNAttributes,
    url,
    variables, // Pass variables to the editor API
    getContent: () => contentManagerRef.current?.getCompleteDocument() || currentContent,
  }

  return (
    <div
      className="min-w-[500px] wysiwyg-editor"
      style={{ position: "relative" }}
    >
      <div className="bg-admin_canvas"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          //backgroundColor: "var(--color-admin_canvas)",
          paddingTop: "20px",
          paddingBottom: "10px",
        }}
      >
        <Typography variant="admin_h4" className="text-center mb-5">
          {title}
        </Typography>

        {!readOnly && <Toolbar editor={editor} />}
      </div>

      <div
        ref={editorRef}
        className="editor w-full border border-admin_bordered rounded-[10px] text-admin_text bg-admin_paper scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper"
        style={{
          minHeight: "400px",
          height: "80vh",
          position: "relative",
          marginBottom: "200px",
          padding: "16px",
          paddingBottom: "100px",
          overflow: "auto",
        }}
        contentEditable={!readOnly}
        suppressContentEditableWarning={true}
        onWheel={handleEditorScroll}
      />

      <SourceEditor
        source={sourceEditorContent}
        isOpen={isSourceEditorOpen}
        onUpdate={(newContent) =>
          handleHtmlUpdate(newContent, {
            fromSourceEditor: true,
            originalContent: sourceEditorContent,
          })
        }
        onClose={() => setIsSourceEditorOpen(false)}
      />
    </div>
  )
}

export default HtmlEditor